import { setUserProperty, trackEvent } from './analytics';
import { playerApiClient } from 'utils/apiClient';
import { tryApi } from 'utils/api/common';
import { getCookie } from 'utils/cookie';
import {
  allAbTests,
  serverSideAndClientSideTestNames,
} from '@daymade/common-lib/abTests';

/** @deprecated See doABTestWithDistribution */
export function doABTest<T extends string>(name: string, choices: T[]): T {
  return doABTestWithDistribution<T>(
    name,
    choices.map((x) => ({ choice: x, weight: 1 })),
  );
}

interface WeightedChoice<T> {
  choice: T;
  weight: number;
  mobileOnly?: boolean;
}

/** You probably want to use 'getABTestGroup' instead of calling this!!!
 *
 * Gets a choice for an AB test group, or uses a weighted choice, if the choice is not in local storage. This DOES NOT
 * check the query string
 */
export function doABTestWithDistribution<T extends string>(
  name: string,
  choices: WeightedChoice<T>[],
) {
  const nameChoices = getAbTestNameChoices(name, choices);

  let choice = getABTestChoiceFromStorage(nameChoices) as T;

  if (!choice) {
    choice = weightedChoice(choices);
    if (choice) {
      storeABTestChoiceToLocalStorage(nameChoices, choice);
      trackABTest(nameChoices, choice);
      const hasLoginCookie = getCookie('jwt', null);
      if (
        hasLoginCookie &&
        serverSideAndClientSideTestNames.includes(
          name as (typeof serverSideAndClientSideTestNames)[number],
        )
      ) {
        // Fire-and-forget upating the player's AB test group on the server
        (async () => {
          const result = await tryApi(
            playerApiClient.auth.setAbTestGroup({
              body: {
                testName: name,
                testValue: choice,
              },
            }),
          );
          if (result.status === 401) {
            // The user may not be logged in, we do not care
          } else if (result.status == 'error') {
            throw new (Error as any)(
              "There was an error updating the player's AB test group",
              { cause: result.error },
            );
          }
        })();
      }
    }
  }
  return choice;
}

type ABTestChoiceString = string & { __type: 'ABTestChoiceString' };

/** Combines the test name and choices into a single string for tracking and storage purposes */
export function getAbTestNameChoices<T extends string>(
  name: string,
  choices: { choice: T }[],
) {
  return `ABTest_${name}_${choices.map((i) => i.choice).join('')}` as ABTestChoiceString;
}

/** Tracks the AB test choice and sets the user property */
export function trackABTest(nameChoices: ABTestChoiceString, choice: string) {
  console.log(nameChoices, choice);
  trackEvent(nameChoices, { choice: choice });
  setUserProperty(nameChoices, choice);
}

export function weightedChoice<T>(choices: WeightedChoice<T>[]): T {
  const isMobile =
    typeof window !== 'undefined' ? window.innerWidth < 768 : true;
  const filteredChoices = choices.filter((x) => {
    if (x.mobileOnly == undefined) {
      return true;
    }
    if (x.mobileOnly) {
      return isMobile;
    }
    return false;
  });
  const totalWeight = filteredChoices.reduce(
    (acc, { weight }) => acc + weight,
    0,
  );
  let rand = Math.random() * totalWeight;

  for (const { choice, weight } of filteredChoices) {
    if (rand < weight) {
      return choice;
    }
    rand -= weight;
  }

  return choices[0].choice; // should never get here but need it as a fallback
}

function getABTestChoiceFromStorage(key: ABTestChoiceString) {
  let choice;
  if (typeof window !== 'undefined') {
    try {
      choice = localStorage.getItem(key);
    } catch (e) {
      console.warn('Failed to read localStorage ' + key, e);
    }
  }

  return choice;
}

function storeABTestChoiceToLocalStorage(
  key: ABTestChoiceString,
  value: string,
) {
  if (typeof window !== 'undefined') {
    localStorage.setItem(key, value);
  }
}

/** Ensures that a given test name and test value are valid according to the values in allAbTests */
export function validateAbTestValue(testName: string, valueForTest: string) {
  const values = allAbTests[testName as keyof typeof allAbTests];
  if (values) {
    if (values.find((x) => x.choice === valueForTest)) {
      const stronglyTypedTestName = testName as keyof typeof allAbTests;
      return {
        testName: stronglyTypedTestName,
        valueForTest,
      } as const;
    }
  }
  return { testName: undefined, valueForTest: undefined };
}

/** This function is intended to be called when we get the abtest_variants value from the server-side. It will
 * add the abtest variants defined in the server side into local storage. Callers should then call
 * abTestStore.updateABTestValuesFromLocalStorage if they are a React component
 */
export function importAbTestVariantsIntoLocalStorage(
  abtestVariants: Record<string, string>,
) {
  console.log(
    'Importing AB test variants into local storage' +
      JSON.stringify(abtestVariants),
  );
  for (const key in abtestVariants) {
    const abTestValidation = validateAbTestValue(key, abtestVariants[key]);
    if (abTestValidation.testName && abTestValidation.valueForTest) {
      const abTestChoiceString = getAbTestNameChoices(
        abTestValidation.testName,
        allAbTests[abTestValidation.testName],
      );
      storeABTestChoiceToLocalStorage(
        abTestChoiceString,
        abTestValidation.valueForTest,
      );
      trackABTest(abTestChoiceString, abTestValidation.valueForTest);
    }
  }
}

/** Reads the AB tests from local storage, and creates a Record of those AB test groups that are expected
 * to be synced between the client-side and server-side.
 *
 * Some tests are client-side only, e.g. those that change some text. The test group is assigned client-side and the
 * backend never needs to know about them
 *
 * Some tests are server-side only, e.g. those that change some API behaviour. The test group is assigned server-side
 * and the front-end only has to vary what is displayed
 *
 * Some tests require both client-side and server-side to know about them. e.g. a test that has its group assigned on
 * the client-side, then the test group must be STORED when the user REGISTERS, and the test group must be RESTORED to
 * the client-side when the user LOGS IN again. When a logged in user gets added to a new test, the value must be sent
 * to the server
 */
export function getClientSideAndServerSideAbTestValues(): Record<
  string,
  string
> {
  const result: Record<string, string> = {};
  for (const testName of serverSideAndClientSideTestNames) {
    const abTestChoiceKey = getAbTestNameChoices(
      testName,
      allAbTests[testName],
    );
    const value = getABTestChoiceFromStorage(abTestChoiceKey);
    if (value) {
      result[testName] = value;
    }
  }
  return result;
}
